<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import type { AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels'
    import type { FormReadModel } from '@Alternatives/Forms/Domain/readmodels'
    import type { ViewSettingsEditorViewModel } from '@Alternatives/AlternativesLists/Components/ViewSettingsEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ViewSettingsEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'saved'): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const alternativesLabel = computed(() => vm.value.alternativesLabel)
    const forms = computed<FormReadModel[]>(() => vm.value.forms)
    const attributes = computed<AttributeReadModel[]>(() => vm.value.attributes)

    const listAttributes = computed(() =>
        attributes.value.filter(a => a.IsList === true)
    )

    // ---------------- writable computed (v-model targets) ----------------

    const formId = computed<number | null>({
        get: () => vm.value.formId,
        set: val => { vm.value.viewSettings.FormId = val }
    })

    const colorAttributeId = computed<number | null>({
        get: () => vm.value.colorAttributeId,
        set: val => { vm.value.viewSettings.ColorAttributeId = val }
    })

    const columnAttributeId = computed<number | null>({
        get: () => vm.value.columnAttributeId,
        set: val => { vm.value.viewSettings.ColumnAttributeId = val }
    })

    // ---------------- methods ----------------

    function confirm()
    {
        vm.value.save()
        emit('saved')
        close()
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Configure View</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label>Form:</label>
                            <select class="form-control" v-model="formId">
                                <option :value="null">All fields (default)</option>
                                <option v-for="f in forms" :value="f.Id">{{f.Name}}</option>
                            </select>
                            <small class="form-text text-muted">
                                You can decide which form you want to open when you edit <em>{{alternativesLabel}}</em>.
                                Simply select the form on the drop-down. If you don't like any of the existing forms, why not create one of your own!
                            </small>
                        </div>

                        <div class="form-group">
                            <label>Color:</label>
                            <select class="form-control" v-model="colorAttributeId">
                                <option :value="null">None (default)</option>
                                <option v-for="a in listAttributes" :value="a.Id">{{a.Name}}</option>
                            </select>
                            <small class="form-text text-muted">
                                You can color-code your <em>{{alternativesLabel}}</em> based on any category-based attribute.
                                This will help you quickly eyeball your <em>{{alternativesLabel}}</em> and and get a feel for how many fall into each category.
                                Simply select which attribute you want to use to color-code your <em>{{alternativesLabel}}</em> within the Kanban.
                            </small>
                        </div>

                        <div class="form-group">
                            <label>Columns:</label>
                            <select class="form-control" v-model="columnAttributeId">
                                <option :value="null">Status (default)</option>
                                <option v-for="a in listAttributes" :value="a.Id">{{a.Name}}</option>
                            </select>
                            <small class="form-text text-muted">
                                By default, your Kanban will break <em>{{alternativesLabel}}</em> out by status.
                                This lets you set out the "workflow" of <em>{{alternativesLabel}}</em>, but sometimes you might want to see <em>{{alternativesLabel}}</em> broken out by other factors.
                                You can select any category-attribute simply by selecting that attribute from this menu... and when you're done, you can come back and change it back to status!
                            </small>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button @click="confirm" type="button" class="btn btn-primary">Confirm</button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>      